import React, { createContext, useContext, useEffect } from 'react';
import { en } from '../translations/en';

/*
 * LanguageContext — English-only mode (2026-07-18).
 *
 * Spanish support has been removed site-wide. This provider still exists
 * so downstream components that call `useLanguage()` (Header, AuthModal,
 * MyRequests, admin dashboards, etc.) keep working without refactor, but
 * every consumer now sees:
 *   • language        : 'en'  (constant)
 *   • t(key)          : English translation only
 *   • setLanguage()   : no-op (silently ignored)
 *   • toggleLanguage(): no-op (silently ignored)
 *
 * Rationale: previously we routed `?lang=es`, `/es/*`, and Accept-Language
 * negotiation through this file. All three now hard-redirect at the edge
 * (server.js) to their English canonical URL, so the client should never
 * observe a Spanish language selection. Locking the client to English is
 * the belt-and-braces guarantee that even if a stale localStorage flag
 * survives, the user still sees English.
 *
 * We do NOT strip `localStorage.rcm_language` here — cheaper to just
 * ignore it. If it ever needs to be cleaned up, `localStorage.removeItem`
 * can be added below without breaking the API.
 */

const LanguageContext = createContext();

const NOOP = () => {};

export const LanguageProvider = ({ children }) => {
  // Force <html lang="en"> on every mount. Guards against a stale
  // client-side setter that once wrote `lang="es"` before this file was
  // rewritten.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = 'en';
    }
    // Clear the legacy localStorage flag once so the browser reflects the
    // English-only stance next reload. Idempotent — safe if already unset.
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('rcm_language') !== 'en') {
        localStorage.setItem('rcm_language', 'en');
      }
    } catch { /* private browsing / storage disabled — harmless */ }
  }, []);

  const t = (key, params) => {
    const keys = String(key || '').split('.');
    let value = en;
    for (const k of keys) {
      value = value?.[k];
    }
    if (typeof value !== 'string') return value || key;
    if (params && typeof params === 'object') {
      return value.replace(/\{(\w+)\}/g, (m, k) => (
        Object.prototype.hasOwnProperty.call(params, k) ? String(params[k]) : m
      ));
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{
      language: 'en',
      setLanguage: NOOP,
      toggleLanguage: NOOP,
      t,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
